import type { MutableRefObject } from "react"
import { useEffect } from "react"
import { useLocation, useNavigate } from "react-router"
import type { Location } from "react-router"
import { ANIMATION_DURATION } from "src/components"
import type { VehicleDetailsStage } from "../VehicleDetailsStage"
import type { VehicleDetailsStageLinkState } from "../VehicleDetailsStageLink"

interface UseScrollToStageProps {
	callbackFn?: () => Promise<void> | void
	editStage: VehicleDetailsStage
	ref: MutableRefObject<HTMLDivElement | null>
}

export const useScrollToStage = ({ callbackFn, editStage, ref }: UseScrollToStageProps) => {
	const { state } = useLocation() as Location<VehicleDetailsStageLinkState | undefined>
	const navigate = useNavigate()

	useEffect(() => {
		// TODO: upon saving/closing an edited stage, we should scroll up to not loose orientation on the page
		if (state?.editStage === editStage) {
			callbackFn?.() // always async except for FinancingContainer

			if (state?.scrollInstantly) {
				scrollElementIntoView(ref.current)
			} else {
				// TODO: 500ms timeout isn't accurate
				// but usually fixes wrong scroll position when navigating from inventoryList
				// where stages above might not be loaded already and can potentially grow or shrink since initial size is based on skeleton
				// more precise would be to scroll again when previous stages are loaded or interval scroll until they are finished
				// callbackFn() returns a promise for the most part which could be handy
				setTimeout(() => scrollElementIntoView(ref.current), ANIMATION_DURATION)
			}

			// Clear editStage from URL after scrolling to it to prevent scrolling again on page refresh
			navigate(
				{},
				{
					preventScrollReset: true,
					replace: true,
					state: {
						...state,
						editStage: undefined,
					},
				},
			)
		}
	}, [state, callbackFn, editStage, ref, navigate])
}

const scrollElementIntoView = (el: HTMLElement | null): void => {
	// offset for sticky header is taken care of via scroll-padding-top style on html element injected via vite.config.ts
	el?.scrollIntoView({ behavior: "smooth", block: "start" })
}
